import { Box, FormControl, FormHelperText, Rating, styled } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
// import { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { Circle, CircleFilled, DragIndicator } from '@tempo-io/sui-icons';
import {
  forwardRef,
  type JSXElementConstructor,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';
import {
  customDatetoIsoDateString as customDatetoIsoDateStringImpl,
  DateFieldInput,
} from './DateFieldInput';
import { DateRangeInput } from './DateRangeFieldInput';
import { FibonacciFieldInput } from './FibonacciFieldInput';
import { MultiSelectFieldInput } from './MultiSelectFieldInput';
import { NumberFieldInput } from './NumberFieldInput';
import { PercentFieldInput } from './PercentFieldInput';
import { SelectFieldInput } from './SelectFieldInput';
import { TextFieldInput } from './TextFieldInput';
import { validateField } from './validationUtils';

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.primary.main,
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.primary.light,
  },
}));
 
export const FlexDiv = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));


export type CustomFieldInputWraperProps = {
  name: string;
  children: ReactNode;
  errorMsg?: string;
  draggable?: boolean;
};

export type CustomFieldInputProps = {
  fieldId: number;
  name: string;
  required?: boolean;
  fieldConfiguration: FieldConfigurationDto;
  values?: string[] | null;
  onUpdateValues?: (values: string[]) => void;
  draggable?: boolean;
  isEditableText?: boolean;
  debounceInterval?: number;
  disableValidate?: boolean;
  slots?: {
    wrapper?: JSXElementConstructor<CustomFieldInputWraperProps>;
  };
  autoFocus?: boolean;
  useDropdown?: boolean;
  onClose?: () => void;
};

export const customDatetoIsoDateString = customDatetoIsoDateStringImpl;

export type CustomFieldInputHandle = {
  validate: () => boolean;
  currentState: () => { values: string[], required: boolean, fieldConfiguration: FieldConfigurationDto}
};

export const CustomFieldInput = forwardRef<CustomFieldInputHandle, CustomFieldInputProps>((props, ref) => {
  const { t } = useTranslation();

  const {
    name,
    required,
    fieldConfiguration,
    values: initialValues,
    onUpdateValues,
    draggable,
    isEditableText,
    debounceInterval = 500,
    disableValidate,
    slots = {},
    autoFocus,
    useDropdown,
    onClose,
  } = props;

  const [values, setValues] = useState<string[]>(initialValues ?? ['']);

  useEffect(() => {
    setValues(initialValues ?? ['']);
  }, [initialValues]);

  const validate = useCallback(() => {
    if (disableValidate) {
      return undefined;
    }
    return validateField(name, fieldConfiguration, required, values, t);
  }, [disableValidate, fieldConfiguration, name, required, t, values]);

  const [errorMsg, setErrorMsg] = useState<string>();

  const validateAndUpdate = useCallback(() => {
    if (fieldConfiguration.fieldType === 'TEXT') {
      const emptyAndNotRequired = String(values[0] || '').trim() === '' && !required;
      if (emptyAndNotRequired) {
        setErrorMsg(undefined);
        return;
      }
      if (errorMsg) {
        const validationError = validate();
        setErrorMsg(validationError);
      }
    } else {
      // For all other fieldTypes (the ones that don't use focusBlur!)
      const validationError = validate();
      setErrorMsg(validationError);

      if (JSON.stringify(initialValues) === JSON.stringify(values)) {
        return;
      }

      if (fieldConfiguration.fieldType !== 'FORMULA' && validationError === undefined) {
        onUpdateValues?.(values);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values,
    errorMsg,
    fieldConfiguration.fieldType,
    initialValues,
    onUpdateValues,
    required,
    validate,
  ]);

  const onTextBlur = useCallback(() => {
    const valuesUnchanged = JSON.stringify(initialValues) === JSON.stringify(values);
    if (valuesUnchanged) {
      setErrorMsg(undefined);
      return;
    }

    const validationError = validate();
    setErrorMsg(validationError);

    if (!validationError) {
      onUpdateValues?.(values);
    }
  }, [values, initialValues, onUpdateValues, validate]);

  useDebounce(values, debounceInterval, validateAndUpdate);

  useImperativeHandle(ref, () => {
    return {
      validate: () => {
        const result = validate();
        setErrorMsg(result);
        return result === undefined;
      },
      currentState: () => {
        return {
          values,
          required: !!required,
          fieldConfiguration,
        };
      },  
    };
  });

  let component: React.ReactNode = null;
  switch (fieldConfiguration.fieldType) {
    case 'NUMBER':
    case 'CURRENCY': {
      component = (
        <NumberFieldInput
          setValues={setValues}
          fieldConfiguration={fieldConfiguration}
          values={values}
          isEditableText={isEditableText}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      );
      break;
    }
    case 'PERCENT': {
      component = (
        <PercentFieldInput
          setValues={setValues}
          fieldConfiguration={fieldConfiguration}
          values={values}
          isEditableText={isEditableText}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      );
      break;
    }
    case 'RATING':
      component = (
        <StyledRating
          data-testid={'fim3-custom-field-input-rating-input'}
          value={parseInt(values[0] ?? '')}
          precision={1}
          icon={<CircleFilled className="size-6" />}
          emptyIcon={<Circle className="size-6" />}
          onChange={(_e: React.SyntheticEvent, value: number | null) => {
            const newValues = value ? [value?.toString()] : [];
            setValues(newValues);
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      );
      break;
    case 'FIBONACCI':
      component = (
        <FibonacciFieldInput
          setValues={setValues}
          fieldConfiguration={fieldConfiguration}
          values={values}
          isEditableText={isEditableText}
          useDropdown={useDropdown}
          required={required}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      );
      break;
    case 'DATE':
      component = (
        <DateFieldInput
          setValues={setValues}
          fieldConfiguration={fieldConfiguration}
          values={values}
          isEditableText={isEditableText}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onClose={onClose}
        />
      );
      break;
    case 'DATE_RANGE':
      component = (
        <DateRangeInput
          values={values}
          onChange={(newValue: DateRange<Date>) => {
            const newValues = [
              customDatetoIsoDateString(newValue[0]) ?? '',
              customDatetoIsoDateString(newValue[1]) ?? '',
            ];
            setValues(newValues);
          }}
          isEditableText={isEditableText}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onClose={onClose}
        />
      );
      break;
    case 'LIST':
      {
        const isMulti = fieldConfiguration.select === 'multi';
        if (isMulti) {
          component = (
            <MultiSelectFieldInput
              setValues={setValues}
              fieldConfiguration={fieldConfiguration}
              values={values}
              isEditableText={isEditableText}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
            />
          );
        } else {
          component = (
            <SelectFieldInput
              setValues={setValues}
              fieldConfiguration={fieldConfiguration}
              values={values}
              isEditableText={isEditableText}
              includeClearOption
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
            />
          );
        }
      }
      break;
    case 'FORMULA':
      component = null; // not rendered here
      break;
    case 'USER':
      component = null; // not rendered here
      break;
    case 'TEXT': {
      component = (
        <TextFieldInput
          setValues={setValues}
          onTextBlur={onTextBlur}
          fieldConfiguration={fieldConfiguration}
          values={values}
          isEditableText={isEditableText}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      );
      break;
    }
  }
  const Wrapper = slots.wrapper ?? DefaultCustomFieldInputWrapper;

  return (
    <Wrapper name={name} draggable={draggable} errorMsg={errorMsg}>
      {component}
    </Wrapper>
  );
});

export const DefaultCustomFieldInputWrapper = ({
  children,
  errorMsg,
  name,
  draggable,
}: CustomFieldInputWraperProps) => (
  <Box
    onClick={(e) => e.stopPropagation()}
    data-testid={`fim3-custom-field-input-${name}`}
    sx={{ width: '100%' }}
  >
    <FlexDiv sx={{ width: '100%' }}>
      {draggable && <span style={{ cursor: 'grab', display: 'flex' }}><DragIndicator className="text-icon-neutral-secondary" /></span>}
      <FormControl
        size="small"
        fullWidth
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </FormControl>
    </FlexDiv>
    {errorMsg && (
      <FormHelperText error data-testid={'fim3-custom-field-input-error-text'}>
        {errorMsg}
      </FormHelperText>
    )}
  </Box>
);
